import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, RNLLoader, useToast } from '../UI';
import { ActionIcon, EyeIcon, PencilIcon, RecallIcon } from '../requisition/ActionIcons';
import { SortTh, useSortState, sortRows } from '../requisition/SortableHeader';
import * as api from '../../api';

// ─── Status configs ───────────────────────────────────────────────────────────
const STATUS_CFG = {
  draft:                  { label: 'Draft',                color: '#64748b' },
  submitted:              { label: 'Submitted',            color: '#2563eb' },
  awaiting_cc_review:     { label: 'Awaiting CC Review',   color: '#d97706' },
  under_cc_review:        { label: 'Under CC Review',      color: '#d97706' },
  partially_reviewed:     { label: 'Partially Reviewed',   color: '#d97706' },
  cc_reviewed:            { label: 'CC Reviewed',          color: '#7c3aed' },
  awaiting_store_review:  { label: 'Awaiting Store Review',color: '#7c3aed' },
  store_reviewed:         { label: 'Store Reviewed',       color: '#0891b2' },
  pending_approval:       { label: 'Pending Approval',     color: '#0891b2' },
  partially_approved:     { label: 'Partially Approved',   color: '#0891b2' },
  approved:               { label: 'Approved',             color: '#16a34a' },
  rejected:               { label: 'Rejected',             color: '#ef4444' },
  on_hold:                { label: 'On Hold',              color: '#64748b' },
  returned:               { label: 'Returned',             color: '#f97316' },
};

const CC_STATUS_CFG = {
  approved: { label: 'Approved', color: '#16a34a', bg: '#dcfce7' },
  rejected: { label: 'Rejected', color: '#ef4444', bg: '#fee2e2' },
  pending:  { label: 'Pending',  color: '#d97706', bg: '#fef3c7' },
};

const getPriorityStyle = (prio) => {
  const p = (prio || 'normal').toLowerCase();
  if (p === 'urgent') return { color: '#ef4444', bg: '#fee2e2', border: '#fca5a5' };
  if (p === 'high')   return { color: '#d97706', bg: '#fef3c7', border: '#fcd34d' };
  return                     { color: '#2563eb', bg: '#dbeafe', border: '#93c5fd' };
};

const fmt  = (n) => n != null ? `₦${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—';
const fmtN = (n) => n != null ? Number(n).toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 4 }) : '';

// ─── Icons ────────────────────────────────────────────────────────────────────
const IcoCheck  = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>;
const IcoX      = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;
const IcoFlag   = ({ filled }) => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/>
    <line x1="4" y1="22" x2="4" y2="15"/>
  </svg>
);

// ─── Inner table columns ──────────────────────────────────────────────────────
const INNER_COLS = [
  { key: 'toggle',   label: '',                       w: 28              },
  { key: 'actions',  label: 'CC Actions',              w: 76              },
  { key: 'cc_stat',  label: 'CC Status',               w: 100, center: true },
  { key: 'flag',     label: '🚩',                      w: 44, center: true },
  { key: 'boq_code', label: 'BOQ Code',                w: 90              },
  { key: 'boq_desc', label: 'BOQ Description',         w: 200             },
  { key: 'mat_code', label: 'Material Code',           w: 110             },
  { key: 'mat_name', label: 'Material Name',           w: 200             },
  { key: 'mat_spec', label: 'Material Specification',  w: 220             },
  { key: 'req_qty',  label: 'Req Qty',                 w: 80, right: true },
  { key: 'uom',      label: 'UOM',                     w: 60, center: true },
  { key: 'est_rate', label: 'Est. Rate',               w: 100, right: true },
  { key: 'est_tot',  label: 'Est. Total',              w: 110, right: true },
  { key: 'cc_qty',   label: 'CC Qty',                  w: 90, right: true },
  { key: 'cc_rate',  label: 'CC Rate',                 w: 100, right: true },
  { key: 'cc_tot',   label: 'CC Total',                w: 110, right: true },
  { key: 'cc_cmt',   label: 'Comment',                 w: 160             },
];
const COL_COUNT = INNER_COLS.length;

// ─── Per-line row ─────────────────────────────────────────────────────────────
function CCLineRow({ li, lineNum, isCC, onDecisionChange, onLineUpdated, copyTrigger }) {
  const initStatus = li.cc_approved ? 'approved'
    : li.cc_line_status === 'rejected' ? 'rejected' : null;

  const [localStatus, setLocalStatus] = useState(initStatus);
  const [localFlag,   setLocalFlag]   = useState(li.cc_flag_potential_variation || false);
  const [open,        setOpen]        = useState(false);
  const [saving,      setSaving]      = useState(false);
  const [ccQty,       setCcQty]       = useState(fmtN(li.cc_quantity) || '');
  const [ccRate,      setCcRate]      = useState(fmtN(li.cc_unit_rate) || '');
  const [ccComment,   setCcComment]   = useState(li.cc_comment || '');

  const ccTotal = ccQty && ccRate ? (parseFloat(ccQty) * parseFloat(ccRate)) : null;
  const hasSubItems = (li.sub_items?.length || 0) > 0;
  const ccCfg = CC_STATUS_CFG[localStatus || 'pending'];

  // Report decision to parent whenever local state changes
  useEffect(() => {
    onDecisionChange?.(li.line_item_id, { localStatus, localFlag });
  }, [localStatus, localFlag]); // eslint-disable-line react-hooks/exhaustive-deps

  // Copy requested values into CC fields when triggered
  useEffect(() => {
    if (!copyTrigger || !isCC) return;
    const qty  = li.required_quantity  != null ? Number(li.required_quantity).toString()  : '';
    const rate = li.estimated_unit_rate != null ? Number(li.estimated_unit_rate).toString() : '';
    setCcQty(qty);
    setCcRate(rate);
    const tot = qty && rate ? parseFloat(qty) * parseFloat(rate) : null;
    api.mrCCReviewLine(li.line_item_id, {
      cc_line_status: localStatus || 'pending',
      cc_quantity: qty ? parseFloat(qty) : null,
      cc_unit_rate: rate ? parseFloat(rate) : null,
      cc_total: tot,
      cc_comment: ccComment || null,
      cc_approved: localStatus === 'approved',
      cc_flag_potential_variation: localFlag,
    }).catch(() => {});
  }, [copyTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleStatus = (status) => {
    if (!isCC) return;
    setLocalStatus(prev => prev === status ? null : status);
  };

  const toggleFlag = () => {
    if (!isCC) return;
    const next = !localFlag;
    setLocalFlag(next);
    if (next && !ccComment) setCcComment('CC flagged variation');
    else if (!next && ccComment === 'CC flagged variation') setCcComment('');
  };

  const saveQtyRate = useCallback(async () => {
    if (!isCC) return;
    setSaving(true);
    try {
      const payload = {
        cc_line_status: localStatus || 'pending',
        cc_quantity: ccQty ? parseFloat(ccQty) : null,
        cc_unit_rate: ccRate ? parseFloat(ccRate) : null,
        cc_total: ccTotal,
        cc_comment: ccComment || null,
        cc_approved: localStatus === 'approved',
        cc_flag_potential_variation: localFlag,
      };
      const updated = await api.mrCCReviewLine(li.line_item_id, payload);
      onLineUpdated?.(updated);
    } catch (_) {}
    setSaving(false);
  }, [isCC, localStatus, localFlag, ccQty, ccRate, ccTotal, ccComment, li.line_item_id]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <tr style={{ background: '#fff', borderBottom: open ? 'none' : '1px solid #e2e8f0', verticalAlign: 'middle' }}>

        {/* Expand toggle */}
        <td style={{ padding: '6px 8px', width: 28, textAlign: 'center', cursor: hasSubItems ? 'pointer' : 'default' }}
            onClick={() => hasSubItems && setOpen(o => !o)}>
          {hasSubItems && (
            <span style={{ fontSize: 9, color: '#64748b', userSelect: 'none', display: 'inline-block', transition: 'transform .15s', transform: open ? 'rotate(90deg)' : 'none' }}>▶</span>
          )}
        </td>

        {/* CC action toggles — Approve + Reject only */}
        <td style={{ padding: '4px 6px', whiteSpace: 'nowrap' }}>
          {isCC ? (
            <div style={{ display: 'flex', gap: 5 }}>
              {/* Approve */}
              <button
                title="Approve line"
                onClick={() => toggleStatus('approved')}
                style={{
                  width: 30, height: 30, borderRadius: 7,
                  border: localStatus === 'approved' ? 'none' : '1.5px solid #16a34a',
                  background: localStatus === 'approved' ? '#16a34a' : '#fff',
                  color: localStatus === 'approved' ? '#fff' : '#16a34a',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', flexShrink: 0, transition: 'all .15s',
                }}
              ><IcoCheck /></button>
              {/* Reject */}
              <button
                title="Reject line"
                onClick={() => toggleStatus('rejected')}
                style={{
                  width: 30, height: 30, borderRadius: 7,
                  border: localStatus === 'rejected' ? 'none' : '1.5px solid #ef4444',
                  background: localStatus === 'rejected' ? '#ef4444' : '#fff',
                  color: localStatus === 'rejected' ? '#fff' : '#ef4444',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', flexShrink: 0, transition: 'all .15s',
                }}
              ><IcoX /></button>
            </div>
          ) : (
            <span style={{ fontSize: 10, color: '#94a3b8' }}>—</span>
          )}
        </td>

        {/* CC Status badge */}
        <td style={{ padding: '6px 8px', textAlign: 'center' }}>
          <span style={{
            background: ccCfg.bg, color: ccCfg.color,
            padding: '3px 10px', borderRadius: 10,
            fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap',
          }}>{ccCfg.label}</span>
        </td>

        {/* Flag — right after CC Status */}
        <td style={{ padding: '6px 8px', textAlign: 'center' }}>
          <button
            title={localFlag ? 'Remove flag' : 'Flag potential variation'}
            onClick={toggleFlag}
            disabled={!isCC}
            style={{
              width: 28, height: 28, borderRadius: 7, border: 'none',
              background: localFlag ? '#fef3c7' : '#f1f5f9',
              color: localFlag ? '#d97706' : '#94a3b8',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: isCC ? 'pointer' : 'default', margin: '0 auto', transition: 'all .15s',
            }}
          ><IcoFlag filled={localFlag} /></button>
        </td>

        {/* BOQ Code */}
        <td style={{ padding: '6px 8px', fontWeight: 700, color: '#1e3a8a', fontSize: 12, whiteSpace: 'nowrap' }}>
          {li.boq_code || '—'}
        </td>

        {/* BOQ Description */}
        <td style={{ padding: '6px 10px', fontSize: 12, color: '#1e293b', fontWeight: 500 }}>
          {li.boq_description || li.remarks || '—'}
        </td>

        {/* Material Code — empty for BOQ lines */}
        <td style={{ padding: '6px 8px', color: '#94a3b8', fontSize: 11, textAlign: 'center' }}>—</td>

        {/* Material Name — empty for BOQ lines */}
        <td style={{ padding: '6px 8px', color: '#94a3b8', fontSize: 11 }}>—</td>

        {/* Material Specification — empty for BOQ lines */}
        <td style={{ padding: '6px 8px', color: '#94a3b8', fontSize: 11 }}>—</td>

        {/* Req Qty */}
        <td style={{ padding: '6px 8px', textAlign: 'right', fontSize: 12, whiteSpace: 'nowrap' }}>
          {li.required_quantity != null ? Number(li.required_quantity).toLocaleString(undefined, { maximumFractionDigits: 4 }) : '—'}
        </td>

        {/* UOM */}
        <td style={{ padding: '6px 8px', textAlign: 'center', fontSize: 11, color: '#475569' }}>{li.unit_of_measure || '—'}</td>

        {/* Est. Rate */}
        <td style={{ padding: '6px 8px', textAlign: 'right', fontSize: 12, color: '#475569' }}>
          {li.estimated_unit_rate != null ? fmt(li.estimated_unit_rate) : '—'}
        </td>

        {/* Est. Total */}
        <td style={{ padding: '6px 8px', textAlign: 'right', fontSize: 12, fontWeight: 700, color: '#1e3a8a', whiteSpace: 'nowrap' }}>
          {li.estimated_total != null ? fmt(li.estimated_total) : '—'}
        </td>

        {/* CC Qty */}
        <td style={{ padding: '4px 6px', textAlign: 'right' }}>
          {isCC ? (
            <input type="number" value={ccQty} onChange={e => setCcQty(e.target.value)} onBlur={saveQtyRate}
              placeholder="0"
              style={{ width: 80, textAlign: 'right', padding: '4px 6px', border: '1px solid #93c5fd', borderRadius: 5, fontSize: 11, background: '#eff6ff', outline: 'none' }} />
          ) : (
            <span style={{ fontSize: 12, color: li.cc_quantity ? '#0284c7' : '#94a3b8' }}>
              {li.cc_quantity != null ? Number(li.cc_quantity).toLocaleString(undefined, { maximumFractionDigits: 4 }) : '—'}
            </span>
          )}
        </td>

        {/* CC Rate */}
        <td style={{ padding: '4px 6px', textAlign: 'right' }}>
          {isCC ? (
            <input type="number" value={ccRate} onChange={e => setCcRate(e.target.value)} onBlur={saveQtyRate}
              placeholder="0"
              style={{ width: 90, textAlign: 'right', padding: '4px 6px', border: '1px solid #93c5fd', borderRadius: 5, fontSize: 11, background: '#eff6ff', outline: 'none' }} />
          ) : (
            <span style={{ fontSize: 12, color: li.cc_unit_rate ? '#0284c7' : '#94a3b8' }}>
              {li.cc_unit_rate != null ? fmt(li.cc_unit_rate) : '—'}
            </span>
          )}
        </td>

        {/* CC Total */}
        <td style={{ padding: '6px 8px', textAlign: 'right', fontSize: 12, fontWeight: 700, color: '#0284c7', whiteSpace: 'nowrap' }}>
          {isCC && ccTotal != null ? fmt(ccTotal) : li.cc_total != null ? fmt(li.cc_total) : '—'}
        </td>

        {/* Comment */}
        <td style={{ padding: '4px 6px', minWidth: 140 }}>
          {isCC ? (
            <input type="text" value={ccComment} onChange={e => setCcComment(e.target.value)} onBlur={saveQtyRate}
              placeholder="Comment…"
              style={{ width: '100%', boxSizing: 'border-box', padding: '4px 6px', border: '1px solid #cbd5e1', borderRadius: 5, fontSize: 11, outline: 'none' }} />
          ) : (
            <span style={{ fontSize: 11, color: '#64748b' }}>{li.cc_comment || '—'}</span>
          )}
        </td>
      </tr>

      {/* Material sub-rows */}
      {open && li.sub_items?.map((sub, si) => (
        <tr key={sub.sub_item_id || si} style={{ background: si % 2 === 0 ? '#fff1f2' : '#fff5f6', borderBottom: '1px solid #fce7f3' }}>
          <td style={{ padding: '5px 8px' }} />
          <td style={{ padding: '5px 8px', textAlign: 'center' }}>
            <span style={{ color: '#be185d', fontWeight: 700, fontSize: 10 }}>└</span>
          </td>
          {/* CC Status — empty */}
          <td style={{ padding: '5px 8px', color: '#94a3b8', fontSize: 11, textAlign: 'center' }}>—</td>
          {/* Flag — empty */}
          <td style={{ padding: '5px 8px', color: '#94a3b8', fontSize: 11, textAlign: 'center' }}>—</td>
          {/* BOQ Code — empty */}
          <td style={{ padding: '5px 8px', color: '#94a3b8', fontSize: 11, textAlign: 'center' }}>—</td>
          {/* BOQ Description — empty */}
          <td style={{ padding: '5px 8px', color: '#94a3b8', fontSize: 11 }}>—</td>
          {/* Material Code */}
          <td style={{ padding: '5px 8px', fontWeight: 700, color: '#be185d', fontSize: 11, whiteSpace: 'nowrap' }}>
            {sub.material_code || '—'}
          </td>
          {/* Material Name — from material_master.name */}
          <td style={{ padding: '5px 8px', fontSize: 11, fontWeight: 600, color: '#1e293b' }}>
            {sub.material_name || sub.material_description || '—'}
          </td>
          {/* Material Specification */}
          <td style={{ padding: '5px 8px', fontSize: 10.5, color: '#475569' }}>
            {sub.specification
              ? <>{sub.specification}{sub.brand_model && <span style={{ color: '#94a3b8', marginLeft: 4 }}>· {sub.brand_model}</span>}</>
              : sub.brand_model || '—'}
          </td>
          <td style={{ padding: '5px 8px', textAlign: 'right', fontSize: 11, fontWeight: 600 }}>
            {sub.required_quantity != null ? Number(sub.required_quantity).toLocaleString(undefined, { maximumFractionDigits: 4 }) : '—'}
          </td>
          <td style={{ padding: '5px 8px', textAlign: 'center', fontSize: 11, color: '#475569' }}>{sub.unit_of_measure || '—'}</td>
          <td style={{ padding: '5px 8px', textAlign: 'right', fontSize: 11 }}>
            {sub.estimated_unit_rate != null ? fmt(sub.estimated_unit_rate) : '—'}
          </td>
          <td style={{ padding: '5px 8px', textAlign: 'right', fontSize: 11, fontWeight: 700, color: '#9f1239' }}>
            {sub.estimated_total != null ? fmt(sub.estimated_total) : '—'}
          </td>
          <td colSpan={4} />
        </tr>
      ))}
      {open && (
        <tr style={{ borderBottom: '2px solid #fda4af' }}>
          <td colSpan={COL_COUNT} style={{ padding: 0 }} />
        </tr>
      )}
    </>
  );
}

// ─── Category section ─────────────────────────────────────────────────────────
function CategorySection({ cat, ci, isCC, onDecisionChange, onLineUpdated, copyTrigger }) {
  const [collapsed, setCollapsed] = useState(false);
  const lines = cat.line_items || [];
  const total    = lines.reduce((s, li) => s + (parseFloat(li.estimated_total) || 0), 0);
  const ccTotal  = lines.reduce((s, li) => s + (parseFloat(li.cc_total) || 0), 0);
  const materials = lines.reduce((s, li) => s + (li.sub_items?.length || 0), 0);

  return (
    <div style={{ marginBottom: 14, borderRadius: 8, border: '1.5px solid #bfdbfe', boxShadow: '0 1px 4px rgba(0,0,0,.04)' }}>

      {/* Category header */}
      <div onClick={() => setCollapsed(c => !c)} style={{
        background: '#1e3a8a', color: '#fff', borderRadius: collapsed ? 6 : '6px 6px 0 0',
        padding: '7px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        cursor: 'pointer', userSelect: 'none',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 10, opacity: 0.7 }}>{collapsed ? '▶' : '▼'}</span>
          <span style={{ fontWeight: 800, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {ci + 1}. {cat.category_name}
          </span>
          <span style={{ fontSize: 10, opacity: 0.75 }}>
            {lines.length} BOQ · {materials} Materials
            {total > 0 ? ` · BOQ ${fmt(total)}` : ''}
          </span>
        </div>
      </div>

      {/* Scrollable table */}
      {!collapsed && (
        <div style={{ overflowX: 'auto', background: '#fff', width: '100%', boxSizing: 'border-box' }}>
          <table style={{ width: 'max-content', minWidth: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#dbeafe', borderBottom: '2px solid #93c5fd' }}>
                {INNER_COLS.map(c => (
                  <th key={c.key} style={{
                    padding: '7px 8px', width: c.w, minWidth: c.w,
                    textAlign: c.center ? 'center' : c.right ? 'right' : 'left',
                    fontWeight: 800, fontSize: 9.5, textTransform: 'uppercase',
                    letterSpacing: '0.06em', color: '#1e3a8a', whiteSpace: 'nowrap',
                    borderRight: '1px solid #bfdbfe',
                  }}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lines.length === 0 ? (
                <tr>
                  <td colSpan={COL_COUNT} style={{ padding: 16, textAlign: 'center', color: '#94a3b8', fontStyle: 'italic', fontSize: 12 }}>
                    No line items in this category.
                  </td>
                </tr>
              ) : (
                lines.map((li, lii) => (
                  <CCLineRow
                    key={li.line_item_id}
                    li={li}
                    lineNum={lii + 1}
                    isCC={isCC}
                    onDecisionChange={onDecisionChange}
                    onLineUpdated={onLineUpdated}
                    copyTrigger={copyTrigger}
                  />
                ))
              )}
            </tbody>
            {ccTotal > 0 && (
              <tfoot>
                <tr style={{ background: '#f0f9ff', borderTop: '1.5px solid #bfdbfe' }}>
                  <td colSpan={12} style={{ padding: '5px 10px', textAlign: 'right', fontSize: 11, fontWeight: 700, color: '#475569' }}>
                    Est. Total: {fmt(total)}
                  </td>
                  <td colSpan={3} style={{ padding: '5px 10px', textAlign: 'right', fontSize: 11, fontWeight: 800, color: '#0284c7' }}>
                    CC Total: {fmt(ccTotal)}
                  </td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Return icon ──────────────────────────────────────────────────────────────
const IcoReturn = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="1 4 1 10 7 10"/>
    <path d="M3.51 15a9 9 0 1 0 .49-3.51"/>
  </svg>
);

// ─── Action bar ───────────────────────────────────────────────────────────────
function ActionBar({ row, onForward, onReturn, onReject, forwardLabel = 'Forward', getDecisions, preparingForward }) {
  const isPreparingThis = preparingForward === row?.request_id;

  const trigger = (type) => {
    if (isPreparingThis) return;
    if (!getDecisions) return;
    const { decisions, unreviewedCount } = getDecisions();
    if (type === 'forward') onForward?.(row, decisions, unreviewedCount);
    else if (type === 'return') onReturn?.(row, decisions, unreviewedCount);
    else if (type === 'reject') onReject?.(row, decisions, unreviewedCount);
  };

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
      borderBottom: '2px solid #bfdbfe', padding: '4px 4px 12px', marginBottom: 14,
    }}>
      <span style={{ fontSize: 11, fontWeight: 800, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.07em', marginRight: 4 }}>
        Actions:
      </span>
      {onForward && (
        <button
          onClick={() => trigger('forward')}
          disabled={isPreparingThis}
          style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '7px 18px', borderRadius: 8,
            border: 'none', background: '#16a34a', color: '#fff', fontSize: 12, fontWeight: 700,
            cursor: isPreparingThis ? 'not-allowed' : 'pointer', opacity: isPreparingThis ? 0.7 : 1,
            transition: 'opacity .15s',
          }}
        >
          {isPreparingThis
            ? <><span style={{ width: 12, height: 12, border: '2px solid rgba(255,255,255,.4)', borderTopColor: '#fff', borderRadius: '50%', display: 'inline-block', animation: 'spin .7s linear infinite' }} /> Preparing…</>
            : <><IcoCheck /> {forwardLabel}</>
          }
        </button>
      )}
      {onReturn && (
        <button onClick={() => trigger('return')} disabled={isPreparingThis} style={{
          display: 'flex', alignItems: 'center', gap: 6, padding: '7px 18px', borderRadius: 8,
          border: 'none', background: '#f97316', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer',
        }}>
          <IcoReturn /> Return
        </button>
      )}
      {onReject && (
        <button onClick={() => trigger('reject')} disabled={isPreparingThis} style={{
          display: 'flex', alignItems: 'center', gap: 6, padding: '7px 18px', borderRadius: 8,
          border: 'none', background: '#ef4444', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer',
        }}>
          <IcoX /> Reject
        </button>
      )}
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

// ─── Expanded review panel ────────────────────────────────────────────────────
function MRExpandedReview({ cachedData, row, onForward, onReturn, onReject, forwardLabel, isCC, preparingForward }) {
  const [data, setData]           = useState(cachedData);
  const [copyTrigger, setCopyTrigger] = useState(0);
  const toast                     = useToast();
  const decisionsRef              = useRef({});

  useEffect(() => { setData(cachedData); }, [cachedData]);

  // Initialize decisions from server data
  useEffect(() => {
    if (!cachedData?.categories) return;
    cachedData.categories.forEach(cat => {
      (cat.line_items || []).forEach(li => {
        if (!decisionsRef.current[li.line_item_id]) {
          decisionsRef.current[li.line_item_id] = {
            localStatus: li.cc_approved ? 'approved' : li.cc_line_status === 'rejected' ? 'rejected' : null,
            localFlag: li.cc_flag_potential_variation || false,
          };
        }
      });
    });
  }, [cachedData]);

  const handleDecisionChange = useCallback((lineItemId, decision) => {
    decisionsRef.current[lineItemId] = decision;
  }, []);

  const getDecisions = useCallback(() => {
    if (!data?.categories) return { decisions: [], unreviewedCount: 0 };
    const allLines = data.categories.flatMap(cat => cat.line_items || []);
    const decisions = allLines.map(li => ({
      line_item_id: li.line_item_id,
      ...(decisionsRef.current[li.line_item_id] || { localStatus: null, localFlag: false }),
    }));
    const unreviewedCount = decisions.filter(d => !d.localStatus).length;
    return { decisions, unreviewedCount };
  }, [data]);

  const handleLineUpdated = (updatedLine) => {
    if (!data) return;
    setData(prev => ({
      ...prev,
      categories: prev.categories.map(cat => ({
        ...cat,
        line_items: cat.line_items.map(li =>
          li.line_item_id === updatedLine.line_item_id ? { ...li, ...updatedLine } : li
        ),
      })),
    }));
  };

  const hasActions = onForward || onReturn || onReject;

  const handleCopyRequested = () => {
    if (!isCC) return;
    const count = data?.categories?.reduce((s, cat) => s + (cat.line_items?.length || 0), 0) || 0;
    setCopyTrigger(t => t + 1);
    toast.show(
      `Requested figures copied into ${count} line(s). Adjust any of them before you forward.`,
      'success',
    );
  };

  if (cachedData === undefined) {
    return (
      <div style={{ padding: '18px 24px', background: '#eff6ff', display: 'flex', alignItems: 'center', gap: 10, color: '#3b82f6', fontSize: 13 }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ animation: 'spin 1s linear infinite' }}>
          <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
        </svg>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        Loading…
      </div>
    );
  }

  if (!data || !data.categories?.length) {
    return (
      <div style={{ background: '#eff6ff', borderTop: '2px solid #bfdbfe', padding: '14px 20px' }}>
        {hasActions && <ActionBar row={row} onForward={onForward} onReturn={onReturn} onReject={onReject} forwardLabel={forwardLabel} getDecisions={getDecisions} preparingForward={preparingForward} />}
        <div style={{ color: '#94a3b8', fontSize: 13, fontStyle: 'italic' }}>No categories found for this request.</div>
      </div>
    );
  }

  const grandEst = data.categories.reduce((s, cat) =>
    s + (cat.line_items || []).reduce((cs, li) => cs + (parseFloat(li.estimated_total) || 0), 0), 0);
  const grandCC = data.categories.reduce((s, cat) =>
    s + (cat.line_items || []).reduce((cs, li) => cs + (parseFloat(li.cc_total) || 0), 0), 0);

  return (
    <div style={{ background: '#eff6ff', borderTop: '2px solid #bfdbfe', padding: '14px 16px 16px', width: '100%', boxSizing: 'border-box' }}>
      {hasActions && (
        <ActionBar
          row={row}
          onForward={onForward}
          onReturn={onReturn}
          onReject={onReject}
          forwardLabel={forwardLabel}
          getDecisions={getDecisions}
          preparingForward={preparingForward}
        />
      )}

      {isCC && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <span style={{ fontSize: 11.5, color: '#64748b', fontWeight: 600 }}>Cost Control values:</span>
          <button
            type="button"
            onClick={handleCopyRequested}
            title="Copy the requested quantity, rate and amount into the CC columns for every line. You can still change any line afterwards."
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '7px 14px', background: '#fff', color: '#1e3a8a',
              border: '1.5px solid #1e3a8a', borderRadius: 8,
              fontSize: 12, fontWeight: 800, cursor: 'pointer',
              fontFamily: 'var(--font)', whiteSpace: 'nowrap', transition: 'all 0.15s',
            }}
            onMouseOver={(ev) => { ev.currentTarget.style.background = '#1e3a8a'; ev.currentTarget.style.color = '#fff'; }}
            onMouseOut={(ev) => { ev.currentTarget.style.background = '#fff'; ev.currentTarget.style.color = '#1e3a8a'; }}
          >
            ⧉ Copy requested values
          </button>
        </div>
      )}

      {data.categories.map((cat, ci) => (
        <CategorySection
          key={cat.category_id}
          cat={cat}
          ci={ci}
          isCC={isCC}
          onDecisionChange={handleDecisionChange}
          onLineUpdated={handleLineUpdated}
          copyTrigger={copyTrigger}
        />
      ))}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 24, paddingTop: 4, fontSize: 13, fontWeight: 800, color: '#1e3a8a' }}>
        {grandEst > 0 && <span>Est. Total: {fmt(grandEst)}</span>}
        {grandCC > 0 && <span style={{ color: '#0284c7' }}>CC Total: {fmt(grandCC)}</span>}
      </div>
    </div>
  );
}

// ─── Main outer table ─────────────────────────────────────────────────────────
export default function MRRequestTable({ rows, loading, onOpen, onView, onRecall, onForward, onReturn, onReject, forwardLabel, isCC, emptyMsg, preparingForward }) {
  const [expandedRows,  setExpandedRows]  = useState({});
  const [detailsCache,  setDetailsCache]  = useState({});
  const sortState = useSortState();

  useEffect(() => {
    if (!rows?.length) return;
    setDetailsCache({});
    setExpandedRows({});
    rows.forEach(r => {
      api.getMaterialRequest(r.request_id)
        .then(res => setDetailsCache(prev => ({ ...prev, [r.request_id]: res?.data || res })))
        .catch(() => setDetailsCache(prev => ({ ...prev, [r.request_id]: null })));
    });
  }, [rows]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleRow = (id) => {
    const opening = !expandedRows[id];
    setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
    if (opening) api.markEntitySeen(id, 'material_request').catch(() => {});
  };

  if (loading) return <RNLLoader title="Loading requests..." subtitle="Fetching data" />;
  if (!rows?.length) return (
    <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-secondary)', fontSize: 13.5 }}>
      {emptyMsg || 'No requests found.'}
    </div>
  );

  const sorted = sortRows(rows, sortState, (r, key) => {
    switch (key) {
      case '_date':       return r.requested_date || r.created_at?.split('T')[0] || '';
      case '_building':   return r.building || '';
      case '_priority':   return r.priority || '';
      case '_cats':       return r.category_count || 0;
      case '_requester':  return r.requested_by_name || '';
      case '_currently':  return r.currently_with || '';
      default:            return r[key];
    }
  });

  return (
    <div>
      <div style={{ fontSize: 13.5, color: 'var(--text-secondary)', marginBottom: 10 }}>
        {rows.length} request{rows.length !== 1 ? 's' : ''} found
      </div>

      <Card style={{ border: '1px solid black', borderRadius: 8, boxShadow: 'none', overflow: 'hidden', padding: 0 }}>
        <div>
          <style>{`
            .mr-req-table th, .mr-req-table td { border: 1px solid #cbd5e1; }
            .mr-req-table thead th { border-color: rgba(255,255,255,0.3); }
          `}</style>
          <table className="mr-req-table" style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#0b318bff', color: '#fff' }}>
                <SortTh label="REF"            sortKey="request_ref" state={sortState} style={{ width: '13%', textAlign: 'left' }} />
                <SortTh label="DATE"           sortKey="_date"       state={sortState} style={{ width: '9%' }} />
                <SortTh label="REQUESTER"      sortKey="_requester"  state={sortState} style={{ width: '13%' }} />
                <SortTh label="BUILDING"       sortKey="_building"   state={sortState} style={{ width: '13%' }} />
                <SortTh label="REV"            sortKey="revision"    state={sortState} style={{ width: '5%' }} />
                <SortTh label="PRIORITY"       sortKey="_priority"   state={sortState} style={{ width: '8%' }} />
                <SortTh label="CATS"           sortKey="_cats"       state={sortState} style={{ width: '5%' }} />
                <SortTh label="STATUS"         sortKey="status"      state={sortState} style={{ width: '13%' }} />
                <SortTh label="CURRENTLY WITH" sortKey="_currently"  state={sortState} style={{ width: '13%' }} />
                <SortTh label="ACTIONS"        style={{ width: '8%' }} />
              </tr>
            </thead>
            <tbody>
              {sorted.map((r) => {
                const isExpanded = !!expandedRows[r.request_id];
                const sc = STATUS_CFG[r.status] || { label: r.status, color: '#64748b' };
                const ps = getPriorityStyle(r.priority);

                return (
                  <React.Fragment key={r.request_id}>
                    <tr onClick={() => toggleRow(r.request_id)} style={{
                      borderBottom: isExpanded ? 'none' : '1px solid var(--border)',
                      background: isExpanded ? '#dbeafe' : undefined,
                      cursor: 'pointer',
                    }}>
                      <td style={{ padding: '12px', fontWeight: 700, color: '#0b318bff', whiteSpace: 'nowrap', userSelect: 'none', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        <span style={{ display: 'inline-block', width: 14, marginRight: 6, color: '#64748b' }}>
                          {isExpanded ? '▼' : '▶'}
                        </span>
                        {r.request_ref || 'DRAFT'}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {r.requested_date || (r.created_at ? r.created_at.split('T')[0] : '—')}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.requested_by_name || '—'}</td>
                      <td style={{ padding: '12px', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.building || '—'}</td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>{r.revision || 'R0'}</td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <span style={{ color: ps.color, background: ps.bg, border: `1px solid ${ps.border}`, padding: '4px 8px', borderRadius: 12, fontSize: 11, fontWeight: 700, display: 'inline-block', textTransform: 'capitalize' }}>
                          {r.priority || 'normal'}
                        </span>
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center', fontWeight: 600 }}>{r.category_count ?? 0}</td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <span style={{ background: sc.color + '18', color: sc.color, border: `1px solid ${sc.color}40`, padding: '4px 8px', borderRadius: 12, fontSize: 11, fontWeight: 700, display: 'inline-block', whiteSpace: 'nowrap' }}>
                          {sc.label.toUpperCase()}
                        </span>
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center', fontSize: 13, color: r.currently_with ? '#1e3a8a' : '#94a3b8', fontWeight: r.currently_with ? 600 : 400, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {r.currently_with || '—'}
                      </td>
                      <td style={{ padding: '12px' }}>
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'center', alignItems: 'center' }}>
                          <ActionIcon title="View Details" color="#0b318bff" onClick={(e) => { e.stopPropagation(); onView?.(r); }}>
                            <EyeIcon />
                          </ActionIcon>
                          <ActionIcon title="Edit / Review" color="#0b318bff" onClick={(e) => { e.stopPropagation(); onOpen?.(r.request_id); }}>
                            <PencilIcon />
                          </ActionIcon>
                          {onRecall && (
                            <ActionIcon title="Recall" color="#dc2626" onClick={(e) => { e.stopPropagation(); onRecall(r); }}>
                              <RecallIcon />
                            </ActionIcon>
                          )}
                        </div>
                      </td>
                    </tr>

                    {isExpanded && (
                      <tr>
                        <td colSpan={10} style={{ padding: 0, border: 'none' }}>
                          <MRExpandedReview
                            cachedData={detailsCache[r.request_id]}
                            row={r}
                            onForward={onForward}
                            onReturn={onReturn}
                            onReject={onReject}
                            forwardLabel={forwardLabel}
                            isCC={isCC}
                            preparingForward={preparingForward}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
